// Global error boundary — catches unhandled route errors and rendering failures

import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, Home, RefreshCw } from 'lucide-react';
import { Logo } from './ui/logo.js';

/** Root error boundary — used as errorElement on the router */
export function RootErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Determine error type
  let status = 500;
  let title = t('error.unexpectedTitle');
  let message = t('error.unexpectedMessage');

  if (isRouteErrorResponse(error)) {
    status = error.status;
    if (status === 404) {
      title = t('error.notFoundTitle');
      message = t('error.notFoundMessage');
    } else if (status === 403) {
      title = t('error.forbiddenTitle');
      message = t('error.forbiddenMessage');
    }
  }

  const is404 = status === 404;

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-950 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <Logo size={48} />
        </div>

        {/* Status code badge */}
        <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-navy-800 px-4 py-1.5 text-sm font-medium text-navy-300">
          <AlertTriangle size={16} className={is404 ? 'text-amber-400' : 'text-red-400'} />
          {status}
        </div>

        <h1 className="mb-2 text-2xl font-bold text-white">{title}</h1>
        <p className="mb-8 text-sm text-navy-400">{message}</p>

        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          {is404 ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500"
            >
              <Home size={16} />
              {t('error.goHome')}
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500"
            >
              <RefreshCw size={16} />
              {t('error.retry')}
            </button>
          )}

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-lg border border-navy-700 px-5 py-2.5 text-sm font-medium text-navy-300 transition-colors hover:bg-navy-800 hover:text-white"
          >
            <ArrowLeft size={16} />
            {t('error.goBack')}
          </button>
        </div>
      </div>
    </div>
  );
}
